import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Download, Eye, Database, FileText, FileSpreadsheet, FileType, ChevronDown, X, ShoppingCart, GripVertical, ClipboardList, Trash2, Plus, Check, Archive, Target, BookOpen, GraduationCap } from 'lucide-react';
import { useT } from '@/store/i18n';
import { useAuth } from '@/store/auth';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SmartSearch } from '@/components/SmartSearch';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Drawer } from '@/components/ui/Drawer';
import { Tabs } from '@/components/ui/Tabs';
import { toast } from '@/components/ui/Toast';
import { QuestionDetailModal } from '@/components/QuestionDetailModal';
import { ExamBuilderModal } from '@/components/ExamBuilderModal';
import { PasswordConfirmModal } from '@/components/PasswordConfirmModal';
import { exportQuestions, downloadSingleQuestion } from '@/lib/export';
import { useDragSort } from '@/hooks/useDragSort';
import { api } from '@/lib/api';
import type { Question, SubjectGroup, GradeLevel, DifficultyLevel } from '@/types';

const CART_KEY = 'baiboon_v8_qbank_cart';

interface QuestionsPayload {
  questions?: Question[];
  pagination?: { total?: number };
}

interface ExamSet {
  id: number;
  name: string;
  description?: string;
  question_count: number;
  created_at: string;
  updated_at: string;
}

interface ExamSetDetail {
  id: number;
  name: string;
  description?: string;
  question_ids: number[];
  metadata?: Record<string, string>;
}

interface Creator {
  id: number;
  full_name: string;
  username: string;
}

interface LearningIndicator {
  id: number;
  code: string;
  title: string;
}

function loadCartFromStorage(): number[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((v) => typeof v === 'number');
  } catch {}
  return [];
}

export function QuestionBank() {
  const t = useT();
  const qc = useQueryClient();
  const role = useAuth((s) => s.user?.role);
  const isAdmin = role === 'admin';
  const typeLabel = (type: string) => t(`q.type.${type}`);

  const [activeTab, setActiveTab] = useState('bank');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [grade, setGrade] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [questionType, setQuestionType] = useState('');
  const [topic, setTopic] = useState('');
  const [creatorId, setCreatorId] = useState('');
  const [indicatorId, setIndicatorId] = useState('');
  const [tagId, setTagId] = useState('');
  const [cart, setCart] = useState<number[]>(loadCartFromStorage);
  const [cartOpen, setCartOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [examBuilderOpen, setExamBuilderOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<number[] | null>(null);

  useEffect(() => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch {}
  }, [cart]);

  const meta = useQuery({
    queryKey: ['metadata'],
    queryFn: () => api<{ data?: { subject_groups?: SubjectGroup[]; grade_levels?: GradeLevel[]; difficulty_levels?: DifficultyLevel[] } }>('metadata/all.php').then((r) => r.data ?? {}),
    staleTime: 5 * 60_000,
  });

  const creators = useQuery({
    queryKey: ['creators'],
    queryFn: () => api<{ data?: { creators?: Creator[] } } & { creators?: Creator[] }>('questions/creators.php').then((r) => r.data?.creators ?? (r as { creators?: Creator[] }).creators ?? []),
    staleTime: 5 * 60_000,
  });

  const indicators = useQuery({
    queryKey: ['learning-indicators', subject, grade],
    queryFn: () => {
      const p = new URLSearchParams();
      if (subject) p.set('subject_group_id', subject);
      if (grade) p.set('grade_level_id', grade);
      return api<{ data?: { indicators?: LearningIndicator[] } } & { indicators?: LearningIndicator[] }>(`learning-indicators.php?${p.toString()}`).then((r) => r.data?.indicators ?? (r as { indicators?: LearningIndicator[] }).indicators ?? []);
    },
    enabled: !!(subject && grade),
    staleTime: 5 * 60_000,
  });

  const tags = useQuery({
    queryKey: ['tags'],
    queryFn: () => api<{ data?: { tags?: Array<{ id: number; name: string; usage_count?: number }> } }>('tags/index.php').then((r) => r.data?.tags ?? []),
    staleTime: 5 * 60_000,
  });

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set('status', 'approved');
    p.set('page', String(page));
    p.set('limit', '20');
    if (search) p.set('search', search);
    if (subject) p.set('subject_group_id', subject);
    if (grade) p.set('grade_level_id', grade);
    if (difficulty) p.set('difficulty_level_id', difficulty);
    if (questionType) p.set('question_type', questionType);
    if (topic) p.set('topic', topic);
    if (creatorId) p.set('creator_id', creatorId);
    if (indicatorId) p.set('learning_indicator_id', indicatorId);
    if (tagId) p.set('tag_id', tagId);
    return p.toString();
  }, [page, search, subject, grade, difficulty, questionType, topic, creatorId, indicatorId, tagId]);

  const list = useQuery({
    queryKey: ['question-bank', params],
    queryFn: () => api<{ data?: QuestionsPayload } & QuestionsPayload>(`questions/index.php?${params}`).then((r) => r.data ?? r),
  });

  const examSets = useQuery({
    queryKey: ['exam-sets'],
    queryFn: () => api<{ data?: { exam_sets?: ExamSet[] } }>('exam-sets/index.php').then((r) => r.data?.exam_sets ?? []),
    enabled: activeTab === 'sets',
    staleTime: 60_000,
  });

  const deleteSet = useMutation({
    mutationFn: (id: number) => api(`exam-sets/index.php?id=${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['exam-sets'] }); toast.success(t('set.success_delete')); },
  });

  const items = list.data?.questions ?? [];
  const total = list.data?.pagination?.total ?? items.length;

  const cartSet = useMemo(() => new Set(cart), [cart]);

  const toggleCart = useCallback((id: number) => {
    setCart((prev) => prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]);
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const allPageInCart = items.length > 0 && items.every((q) => cartSet.has(q.id));

  function togglePageSelect() {
    if (allPageInCart) {
      const pageIds = new Set(items.map((q) => q.id));
      setCart((prev) => prev.filter((id) => !pageIds.has(id)));
    } else {
      const toAdd = items.filter((q) => !cartSet.has(q.id)).map((q) => q.id);
      setCart((prev) => [...prev, ...toAdd]);
    }
  }

  function exportAs(format: 'excel' | 'pdf' | 'csv' | 'zip_by_type') {
    if (cart.length === 0) { toast.warning(t('qbank.select_first')); return; }
    exportQuestions(cart, format);
    toast.info(`${t('qbank.exporting')} ${cart.length} ${t('units.questions')} ${format.toUpperCase()}`);
  }

  async function loadSetIntoCart(setId: number) {
    try {
      const r = await api<{ data?: { exam_set?: ExamSetDetail } }>(`exam-sets/index.php?id=${setId}`);
      const ids = r.data?.exam_set?.question_ids ?? [];
      if (ids.length === 0) return;
      setCart(ids);
      setActiveTab('bank');
      toast.success(t('set.loaded_cart'));
    } catch { toast.error(t('error.load_failed')); }
  }

  async function handleDeleteConfirm(password: string) {
    if (!pendingDelete || pendingDelete.length === 0) return;
    const res = await api<{ data?: { deleted_count?: number }; message?: string }>(
      'questions/manage.php',
      {
        method: 'DELETE',
        json: { password, question_ids: pendingDelete },
      }
    );
    const count = res.data?.deleted_count ?? pendingDelete.length;
    qc.invalidateQueries({ queryKey: ['question-bank'] });
    setCart((prev) => prev.filter((id) => !pendingDelete.includes(id)));
    toast.success(t('delete.success').replace('{n}', String(count)));
    setPendingDelete(null);
  }

  const questionTypes: Array<{ value: string; label: string }> = [
    { value: 'single_choice', label: typeLabel('single_choice') },
    { value: 'multiple_answer', label: typeLabel('multiple_answer') },
    { value: 'true_false', label: typeLabel('true_false') },
    { value: 'matching', label: typeLabel('matching') },
    { value: 'essay', label: typeLabel('essay') },
    { value: 'short_answer', label: typeLabel('short_answer') },
  ];

  const deleteModalTitle = pendingDelete
    ? pendingDelete.length === 1
      ? t('delete.title_single')
      : t('delete.title_bulk').replace('{n}', String(pendingDelete.length))
    : '';

  const deleteWarningItems = pendingDelete
    ? pendingDelete.map((id) => {
        const q = items.find((q) => q.id === id);
        return q ? (q.question_text?.slice(0, 80) ?? `#${id}`) : `#${id}`;
      })
    : [];

  const bankContent = (
    <div className="space-y-4">
      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <SmartSearch
            value={search}
            onChange={(v) => { setSearch(v); setPage(1); }}
            placeholder={t('qbank.search')}
          />
          <Select value={subject} onChange={(e) => { setSubject(e.target.value); setIndicatorId(''); setPage(1); }}>
            <option value="">{t('filter.all_subjects')}</option>
            {(meta.data?.subject_groups ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
          <Select value={grade} onChange={(e) => { setGrade(e.target.value); setIndicatorId(''); setPage(1); }}>
            <option value="">{t('filter.all_grades')}</option>
            {(meta.data?.grade_levels ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </Select>
          <Select value={difficulty} onChange={(e) => { setDifficulty(e.target.value); setPage(1); }}>
            <option value="">{t('filter.all_difficulties')}</option>
            {(meta.data?.difficulty_levels ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
          <Select value={questionType} onChange={(e) => { setQuestionType(e.target.value); setPage(1); }}>
            <option value="">{t('qbank.filter.all_types')}</option>
            {questionTypes.map((qt) => <option key={qt.value} value={qt.value}>{qt.label}</option>)}
          </Select>
          <Input
            placeholder={t('qbank.filter.topic_ph')}
            value={topic}
            onChange={(e) => { setTopic(e.target.value); setPage(1); }}
          />
          <Select value={creatorId} onChange={(e) => { setCreatorId(e.target.value); setPage(1); }}>
            <option value="">{t('qbank.filter.all_creators')}</option>
            {(creators.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.full_name || c.username}</option>)}
          </Select>
          {subject && grade ? (
            <Select value={indicatorId} onChange={(e) => { setIndicatorId(e.target.value); setPage(1); }}>
              <option value="">{t('qbank.filter.all_indicators')}</option>
              {(indicators.data ?? []).map((ind) => <option key={ind.id} value={ind.id}>{ind.code} — {ind.title}</option>)}
            </Select>
          ) : (
            <div />
          )}
          <Select value={tagId} onChange={(e) => { setTagId(e.target.value); setPage(1); }}>
            <option value="">{t('tag.filter')}</option>
            {(tags.data ?? []).map((tg) => <option key={tg.id} value={tg.id}>{tg.name}{tg.usage_count ? ` (${tg.usage_count})` : ''}</option>)}
          </Select>
        </div>
      </Card>

      {/* Bulk toolbar */}
      <div className="flex items-center justify-between gap-3 px-1 flex-wrap">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 accent-brand-600"
              checked={allPageInCart && items.length > 0}
              onChange={togglePageSelect}
              disabled={items.length === 0}
            />
            {allPageInCart ? t('qbank.bulk.deselect_page') : t('qbank.bulk.select_page')}
          </label>
          {cart.length > 0 && (
            <span className="text-xs text-brand-600 font-medium">
              {t('qbank.bulk.selected').replace('{n}', String(cart.length))}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {cart.length > 0 && (
            <>
              <Button variant="outline" size="sm" leftIcon={<ShoppingCart className="w-3.5 h-3.5" />} onClick={() => setCartOpen(true)}>
                {t('qbank.bulk.open_cart')}
              </Button>
              {isAdmin && (
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<Trash2 className="w-3.5 h-3.5" />}
                  onClick={() => setPendingDelete([...cart])}
                >
                  {t('qbank.bulk.delete_selected')}
                </Button>
              )}
            </>
          )}
          <span className="text-xs text-slate-400">{total.toLocaleString('th-TH')} {t('units.questions')}</span>
        </div>
      </div>

      {/* Table */}
      {list.isLoading ? (
        <Card className="p-4 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Database className="w-7 h-7" />}
            title={t('qbank.empty')}
            description={t('qbank.empty_desc')}
          />
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="w-10 px-3 py-2.5 text-center font-medium text-slate-500 text-xs">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 accent-brand-600"
                    checked={allPageInCart && items.length > 0}
                    onChange={togglePageSelect}
                  />
                </th>
                <th className="hidden sm:table-cell w-12 px-2 py-2.5 text-center font-medium text-slate-500 text-xs">{t('qbank.table.col_image')}</th>
                <th className="w-12 px-2 py-2.5 text-center font-medium text-slate-500 text-xs">{t('qbank.table.col_no')}</th>
                <th className="px-3 py-2.5 text-left font-medium text-slate-500 text-xs">{t('qbank.table.col_question')}</th>
                <th className="hidden md:table-cell px-2 py-2.5 text-left font-medium text-slate-500 text-xs">{t('qbank.table.col_subject')}</th>
                <th className="hidden md:table-cell px-2 py-2.5 text-left font-medium text-slate-500 text-xs">{t('qbank.table.col_grade')}</th>
                <th className="hidden lg:table-cell px-2 py-2.5 text-left font-medium text-slate-500 text-xs">{t('qbank.table.col_type')}</th>
                <th className="hidden lg:table-cell px-2 py-2.5 text-left font-medium text-slate-500 text-xs">{t('qbank.table.col_difficulty')}</th>
                <th className="hidden xl:table-cell px-2 py-2.5 text-left font-medium text-slate-500 text-xs">{t('qbank.table.col_creator')}</th>
                <th className="hidden xl:table-cell w-16 px-2 py-2.5 text-center font-medium text-slate-500 text-xs">{t('qbank.table.col_usage')}</th>
                <th className="px-3 py-2.5 text-right font-medium text-slate-500 text-xs">{t('qbank.table.col_actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((q, idx) => {
                const inCart = cartSet.has(q.id);
                const usageCount = (q as Question & { usage_count?: number }).usage_count ?? 0;
                const rowNum = (page - 1) * 20 + idx + 1;
                const truncated = (q.question_text ?? '').length > 80
                  ? q.question_text!.slice(0, 80) + '…'
                  : (q.question_text ?? '—');
                return (
                  <tr
                    key={q.id}
                    className={`transition-colors even:bg-slate-50/50 hover:bg-brand-50/30 ${inCart ? 'bg-brand-50/40 even:bg-brand-50/40' : ''}`}
                  >
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 accent-brand-600"
                        checked={inCart}
                        onChange={() => toggleCart(q.id)}
                      />
                    </td>
                    <td className="hidden sm:table-cell px-2 py-2 text-center">
                      {q.image_path ? (
                        <img
                          src={`/${q.image_path.replace(/^\.?\//, '')}`}
                          alt=""
                          loading="lazy"
                          className="w-10 h-10 rounded-lg object-cover border border-slate-200 mx-auto"
                        />
                      ) : (
                        <span className="text-slate-300 text-xs">{t('qbank.table.no_image')}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center text-xs text-slate-400 font-mono">{rowNum}</td>
                    <td className="px-3 py-2 max-w-xs">
                      <span
                        className="text-slate-800 text-sm leading-snug cursor-default"
                        title={q.question_text ?? ''}
                      >
                        {truncated}
                      </span>
                    </td>
                    <td className="hidden md:table-cell px-2 py-2">
                      {q.subject_name ? <Badge tone="brand">{q.subject_name}</Badge> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="hidden md:table-cell px-2 py-2">
                      {q.grade_name ? <Badge tone="info">{q.grade_name}</Badge> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="hidden lg:table-cell px-2 py-2">
                      {q.question_type ? <Badge tone="neutral">{typeLabel(q.question_type)}</Badge> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="hidden lg:table-cell px-2 py-2">
                      {q.difficulty_level ? <Badge tone="warning">{q.difficulty_level}</Badge> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="hidden xl:table-cell px-2 py-2 text-xs text-slate-500 truncate max-w-[100px]">
                      {q.creator_name ?? '—'}
                    </td>
                    <td className="hidden xl:table-cell px-2 py-2 text-center">
                      {usageCount > 0 ? (
                        <span className="text-xs text-slate-500 flex items-center justify-center gap-1">
                          <ClipboardList className="w-3 h-3" />
                          {t('qbank.table.usage_sets').replace('{n}', String(usageCount))}
                        </span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1 flex-nowrap">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('btn.view')}
                          onClick={() => setDetailId(q.id)}
                          className="h-7 w-7"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('btn.download_pdf')}
                          onClick={() => downloadSingleQuestion(q.id, 'pdf')}
                          className="h-7 w-7"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t('btn.delete')}
                            onClick={() => setPendingDelete([q.id])}
                            className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          variant={inCart ? 'primary' : 'secondary'}
                          size="sm"
                          onClick={() => toggleCart(q.id)}
                          className="h-7 text-xs px-2 gap-1"
                        >
                          {inCart ? <><Check className="w-3 h-3" />{t('qbank.in_cart')}</> : <><Plus className="w-3 h-3" />{t('qbank.add')}</>}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {items.length > 0 && (
        <Pagination page={page} pageSize={20} total={total} onPageChange={setPage} />
      )}
    </div>
  );

  const setsContent = (
    <div className="space-y-4">
      {examSets.isLoading ? (
        Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="p-5">
            <Skeleton className="h-4 w-1/2 mb-2" />
            <Skeleton className="h-3 w-full" />
          </Card>
        ))
      ) : (examSets.data ?? []).length === 0 ? (
        <Card>
          <EmptyState
            icon={<ClipboardList className="w-7 h-7" />}
            title={t('set.empty')}
            description={t('set.empty_desc')}
          />
        </Card>
      ) : (
        (examSets.data ?? []).map((es) => (
          <Card key={es.id} className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 truncate">{es.name}</p>
                {es.description && <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{es.description}</p>}
                <p className="text-xs text-slate-400 mt-2">
                  {t('set.q_count').replace('{n}', String(es.question_count))} · {new Date(es.created_at).toLocaleDateString('th-TH')}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="secondary" size="sm" onClick={() => loadSetIntoCart(es.id)}>
                  {t('set.btn.load_cart')}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('set.btn.delete')}
                  onClick={() => deleteSet.mutate(es.id)}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          </Card>
        ))
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('qbank.title')}
        description={`${t('qbank.subtitle')} · ${total.toLocaleString('th-TH')} ${t('units.questions')}`}
        actions={
          <div className="flex items-center gap-2">
            {cart.length > 0 && (
              <>
                <Button variant="ghost" size="sm" leftIcon={<X className="w-3.5 h-3.5" />} onClick={clearCart}>
                  {t('qbank.clear_cart')} ({cart.length})
                </Button>
                <Button variant="outline" size="sm" leftIcon={<ShoppingCart className="w-4 h-4" />} onClick={() => setCartOpen(true)}>
                  {t('qbank.view_cart')}
                </Button>
                <Button size="sm" onClick={() => setExamBuilderOpen(true)}>
                  {t('qbank.create_paper')}
                </Button>
                <ExportMenu count={cart.length} onExport={exportAs} t={t} />
              </>
            )}
          </div>
        }
      />

      <Tabs
        tabs={[
          { id: 'bank', label: t('qbank.tab.bank'), content: bankContent },
          { id: 'sets', label: t('qbank.tab.sets'), content: setsContent },
        ]}
        defaultId="bank"
        onChange={setActiveTab}
      />

      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        setCart={setCart}
        questions={items}
        t={t}
        onCreatePaper={() => { setCartOpen(false); setExamBuilderOpen(true); }}
      />

      <QuestionDetailModal questionId={detailId} onClose={() => setDetailId(null)} />

      <ExamBuilderModal
        open={examBuilderOpen}
        onClose={() => setExamBuilderOpen(false)}
        cart={cart}
        questions={items}
      />

      <PasswordConfirmModal
        open={pendingDelete !== null}
        title={deleteModalTitle}
        description={t('delete.desc')}
        warningItems={deleteWarningItems}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDeleteConfirm}
      />

      {cart.length > 0 && !cartOpen && (
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-brand-600 hover:bg-brand-700 text-white rounded-full shadow-lg flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all"
        >
          <ShoppingCart className="w-4 h-4" />
          {cart.length}
        </button>
      )}
    </div>
  );
}

// ─── Cart Drawer ──────────────────────────────────────
function CartDrawer({
  open, onClose, cart, setCart, questions, t, onCreatePaper,
}: {
  open: boolean;
  onClose: () => void;
  cart: number[];
  setCart: React.Dispatch<React.SetStateAction<number[]>>;
  questions: Question[];
  t: (k: string) => string;
  onCreatePaper: () => void;
}) {
  const cartQuestions = cart.map((id) => {
    const q = questions.find((q) => q.id === id);
    return q ?? ({ id, question_text: `#${id}`, question_type: 'single_choice', status: 'approved', created_at: '' } as Question);
  });

  const reorder = useCallback((from: number, to: number) => {
    setCart((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }, [setCart]);

  const drag = useDragSort(cart.length, reorder);

  const totalPoints = cartQuestions.reduce((sum, q) => sum + (q.points ?? 1), 0);

  // Unique coverage counts
  const uniqueIndicatorCount = useMemo(() => {
    const ids = new Set<number>();
    for (const q of cartQuestions) {
      for (const ind of q.learning_indicators ?? []) {
        ids.add(ind.id);
      }
    }
    return ids.size;
  }, [cartQuestions]);

  const uniqueSubjectCount = useMemo(() => {
    const names = new Set<string>();
    for (const q of cartQuestions) {
      if (q.subject_name) names.add(q.subject_name);
    }
    return names.size;
  }, [cartQuestions]);

  const uniqueGradeCount = useMemo(() => {
    const names = new Set<string>();
    for (const q of cartQuestions) {
      if (q.grade_name) names.add(q.grade_name);
    }
    return names.size;
  }, [cartQuestions]);

  const diffCounts = cartQuestions.reduce<Record<string, number>>((acc, q) => {
    const k = q.difficulty_level || '—';
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});

  const typeCounts = cartQuestions.reduce<Record<string, number>>((acc, q) => {
    const k = q.question_type || '—';
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});

  const maxDiff = Math.max(1, ...Object.values(diffCounts));
  const maxType = Math.max(1, ...Object.values(typeCounts));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('qbank.cart_title').replace('{n}', String(cart.length))}
      width={420}
    >
      {cart.length === 0 ? (
        <div className="p-6 text-center text-slate-500">
          <ShoppingCart className="w-8 h-8 mx-auto mb-3 text-slate-300" />
          <p className="text-sm font-medium">{t('qbank.cart_empty')}</p>
          <p className="text-xs mt-1">{t('qbank.cart_empty_desc')}</p>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {/* Summary charts */}
          <div className="px-4 py-3 border-b border-slate-100 space-y-3 bg-slate-50/60">
            {/* Coverage chips */}
            <div>
              <p className="text-xs text-slate-500 mb-1.5 font-medium">{t('qbank.cart_coverage_label')}</p>
              <div className="grid grid-cols-3 gap-1.5">
                <div className="flex flex-col items-center gap-0.5 bg-white rounded-lg border border-slate-100 px-2 py-1.5">
                  <Target className="w-3.5 h-3.5 text-brand-500" />
                  <span className="text-sm font-bold text-slate-900 leading-none">{uniqueIndicatorCount}</span>
                  <span className="text-[10px] text-slate-400 text-center leading-tight">{t('qbank.cart_indicators')}</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 bg-white rounded-lg border border-slate-100 px-2 py-1.5">
                  <BookOpen className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-sm font-bold text-slate-900 leading-none">{uniqueSubjectCount}</span>
                  <span className="text-[10px] text-slate-400 text-center leading-tight">{t('qbank.cart_subjects')}</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 bg-white rounded-lg border border-slate-100 px-2 py-1.5">
                  <GraduationCap className="w-3.5 h-3.5 text-violet-500" />
                  <span className="text-sm font-bold text-slate-900 leading-none">{uniqueGradeCount}</span>
                  <span className="text-[10px] text-slate-400 text-center leading-tight">{t('qbank.cart_grades')}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span className="font-medium">{t('qbank.cart_total_pts')}</span>
              <span className="font-bold text-slate-900">{totalPoints}</span>
            </div>
            {Object.keys(diffCounts).length > 0 && (
              <div>
                <p className="text-xs text-slate-500 mb-1.5 font-medium">{t('qbank.cart_by_difficulty')}</p>
                {Object.entries(diffCounts).map(([label, count]) => (
                  <div key={label} className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-slate-600 w-20 truncate">{label}</span>
                    <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-brand-500 rounded-full" style={{ width: `${(count / maxDiff) * 100}%` }} />
                    </div>
                    <span className="text-xs text-slate-500 w-4 text-right">{count}</span>
                  </div>
                ))}
              </div>
            )}
            {Object.keys(typeCounts).length > 0 && (
              <div>
                <p className="text-xs text-slate-500 mb-1.5 font-medium">{t('qbank.cart_by_type')}</p>
                {Object.entries(typeCounts).map(([label, count]) => (
                  <div key={label} className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-slate-600 w-20 truncate">{t(`q.type.${label}`)}</span>
                    <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(count / maxType) * 100}%` }} />
                    </div>
                    <span className="text-xs text-slate-500 w-4 text-right">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ordered list */}
          <ol className="flex-1 overflow-y-auto py-2">
            {cartQuestions.map((q, i) => {
              const dh = drag.handlers(i);
              return (
                <li
                  key={q.id}
                  draggable
                  onDragStart={dh.onDragStart}
                  onDragOver={dh.onDragOver}
                  onDragLeave={dh.onDragLeave}
                  onDrop={dh.onDrop}
                  onDragEnd={dh.onDragEnd}
                  className={`flex items-start gap-3 px-4 py-2.5 cursor-grab active:cursor-grabbing border-b border-slate-100 last:border-0 transition-colors ${drag.dragOver === i ? 'border-t-2 border-brand-500 bg-brand-50/40' : ''} ${drag.dragging === i ? 'opacity-40' : ''}`}
                >
                  <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
                  <span className="text-xs text-slate-400 flex-shrink-0 w-5 pt-0.5">{i + 1}.</span>
                  <p className="flex-1 text-sm text-slate-800 line-clamp-2">{q.question_text || `#${q.id}`}</p>
                  <button
                    type="button"
                    className="flex-shrink-0 text-slate-300 hover:text-red-500 transition-colors"
                    onClick={() => setCart((prev) => prev.filter((id) => id !== q.id))}
                    aria-label={t('btn.remove')}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              );
            })}
          </ol>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-slate-100 flex gap-2">
            <Button variant="secondary" size="sm" className="flex-1" onClick={onClose}>
              {t('btn.close')}
            </Button>
            <Button size="sm" className="flex-1" onClick={onCreatePaper}>
              {t('qbank.create_paper')}
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
}

// ─── Export menu ──────────────────────────────────────
function ExportMenu({
  count, onExport, t,
}: {
  count: number;
  onExport: (f: 'excel' | 'pdf' | 'csv' | 'zip_by_type') => void;
  t: (k: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <Button size="sm" leftIcon={<Download className="w-4 h-4" />} rightIcon={<ChevronDown className="w-3.5 h-3.5" />} onClick={() => setOpen((v) => !v)}>
        {t('qbank.export')} {count}
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-48 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-10 animate-slide-down">
          <button
            type="button"
            onClick={() => { onExport('excel'); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
            <span>Excel (.xlsx)</span>
          </button>
          <button
            type="button"
            onClick={() => { onExport('pdf'); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <FileText className="w-4 h-4 text-red-600" />
            <span>PDF (.pdf)</span>
          </button>
          <button
            type="button"
            onClick={() => { onExport('csv'); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <FileType className="w-4 h-4 text-blue-600" />
            <span>CSV (.csv)</span>
          </button>
          <button
            type="button"
            onClick={() => { onExport('zip_by_type'); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <Archive className="w-4 h-4 text-violet-600" />
            <span>{t('qbank.export_zip')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
